import { useEffect, useSyncExternalStore, type RefObject } from "react";

/** 開いているダイアログ (aria-modal) のうち、いちばん後ろに足された枠が最前面。引き出しの上に確かめを開いたとき、下の枠はキーを扱わない */
export function isTopmostModal(root: HTMLElement): boolean {
  const all = document.querySelectorAll('[aria-modal="true"]');
  const top = all[all.length - 1];
  return !top || root.contains(top) || top.contains(root);
}

// 動画の枠 (iframe) と再生の操作つきの video、audio も Tab の巡りに入れる (再生後に「閉じる」だけが残って動画へ移れなくなる)
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, video[controls], audio[controls], [tabindex]:not([tabindex="-1"])';

/**
 * 開いた枠のフォーカスの扱い。出てきたとき枠の中の最初の操作へ移し、閉じるとき (アンマウント) 開く前の要素へ戻す。
 * trap なら Tab を枠の中で巡らせる (モーダル)。小窓は trap 無しで、外へ Tab で出られる
 */
export function useFocusScope(ref: RefObject<HTMLElement | null>, { trap, manage = true }: { trap: boolean; manage?: boolean }): void {
  useEffect(() => {
    const root = ref.current;
    // manage=false (幕なしの横の面) はフォーカスを動かさない。開いたあとも表の行で送り、閉じても今いる所に残る
    if (!root || !manage) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // 実際に Tab で止まれる要素だけ (tabindex=-1、hidden、非表示、inert の中を除く)
    const items = () =>
      [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => {
        // 動画の要素は tabIndex が -1 と読める環境がある。外すのは tabindex="-1" と明示したときだけ
        const media = el.matches("iframe, video, audio");
        if ((media ? el.getAttribute("tabindex") === "-1" : el.tabIndex < 0) || el.hasAttribute("hidden") || el.closest("[inert], [hidden]")) return false;
        if (el instanceof HTMLInputElement && el.type === "hidden") return false;
        // 自分も祖先も、描かれている (display:none や visibility:hidden の中に無い)
        for (let node: HTMLElement | null = el; node && node !== root.parentElement; node = node.parentElement) {
          const style = getComputedStyle(node);
          if (style.display === "none" || (node === el && style.visibility === "hidden")) return false;
        }
        return true;
      });
    (items()[0] ?? root).focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (!trap || e.key !== "Tab" || !isTopmostModal(root)) return;
      const list = items();
      if (list.length === 0) {
        e.preventDefault();
        return;
      }
      const active = document.activeElement;
      const outside = !(active instanceof Node) || !root.contains(active);
      if (e.shiftKey && (active === list[0] || outside)) {
        e.preventDefault();
        list[list.length - 1].focus();
      } else if (!e.shiftKey && (active === list[list.length - 1] || outside)) {
        e.preventDefault();
        list[0].focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (previous && document.contains(previous)) previous.focus();
    };
    // 開いた 1 回だけ。中身が描き直されても、フォーカスを奪い直さない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/*
  開いている LgModal (確かめ) の数。引き出しが「上に確かめが開いている」と知るために読む。
  確かめの幕が引き出しも含めて全部を覆うので、そのとき引き出し自身の幕は消して二重に暗くしない
*/
let openModals = 0;
const listeners = new Set<() => void>();
function setOpenModals(next: number) {
  openModals = next;
  listeners.forEach((l) => l());
}
/** LgModal が開いている間だけ呼ぶ。閉じ始めで数が減るので、幕の薄れに合わせて引き出しの幕が戻る */
export function useModalOpenMark(open: boolean): void {
  useEffect(() => {
    if (!open) return;
    setOpenModals(openModals + 1);
    return () => setOpenModals(openModals - 1);
  }, [open]);
}
/** いま LgModal が開いているか */
export function useAnyModalOpen(): boolean {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => void listeners.delete(cb);
    },
    () => openModals > 0,
    () => false,
  );
}
